import { useFieldArray, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import { useGetCurrencies } from "api/queries/common/useGetCurrencies";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import Checkbox from "components/v2/Checkbox/Checkbox";
import Input from "components/v2/Input/Input";
import InputDropdownGroup from "components/v2/InputDropdownGroup/InputDropdownGroup";
import Button from "components/v2/Button/Button";
import Divider from "components/v2/Divider/Divider";

interface PackingListCustomsInfoFormProps {
  index: number;
}

export default function PackingListCustomsInfoForm({ index }: PackingListCustomsInfoFormProps) {
  const intl = useIntl();
  const { watch, control, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `packing_list.${index}.good_customs_information`,
  });

  const { data: currenciesData }: any = useGetCurrencies({
    select: (data) =>
      data.map((currency) => ({
        value: currency.code,
        label: `(${currency.code}) ${currency.name}`,
      })) as any,
  });

  const handleAddCommodity = () => {
    append({ description: "", hs_code: "", customs_value: "", currency_code: "USD" });
  };

  const handleRemoveCommodity = (fieldIndex: number) => {
    remove(fieldIndex);
  };

  const handleSelectCurrency = (fieldIndex: number, value: string) => {
    setValue(`packing_list.${index}.good_customs_information.${fieldIndex}.currency_code`, value);
  };

  return (
    <div>
      <div className="width-fit">
        <Checkbox
          name={`packing_list.${index}.customs_info_enabled`}
          label={intl.formatMessage({
            id: "new-request.packing-list.customsInfo",
            defaultMessage: "Customs info",
          })}
          size="sm"
        />
      </div>
      {watch(`packing_list.${index}.customs_info_enabled`) && (
        <>
          {fields.map((field, fieldIndex) => (
            <div key={field.id} className="mt-md">
              <div className="d-flex gap-xl align-items-end">
                <Input
                  name={`packing_list.${index}.good_customs_information.${fieldIndex}.description`}
                  placeholder="Item description"
                  label={intl.formatMessage({
                    id: "new-request.packing-list.commodityInformation",
                    defaultMessage: "Commodity information",
                  })}
                />
                <div style={{ width: "32px" }}>
                  <Button
                    iconLeft={<TrashIcon />}
                    variant="link-error"
                    className="pb-lg"
                    onClick={() => handleRemoveCommodity(fieldIndex)}
                  />
                </div>
              </div>

              <div className="d-flex gap-xl mt-xl" style={{ maxWidth: "500px" }}>
                <Input
                  name={`packing_list.${index}.good_customs_information.${fieldIndex}.hs_code`}
                  label={intl.formatMessage({
                    id: "new-request.packing-list.hsCode",
                    defaultMessage: "HS Code",
                  })}
                />

                <InputDropdownGroup
                  names={[`packing_list.${index}.good_customs_information.${fieldIndex}.value`]}
                  options={currenciesData || []}
                  dropdownName={`packing_list.${index}.good_customs_information.${fieldIndex}.currency_code`}
                  label={intl.formatMessage({
                    id: "new-request.packing-list.customCommodityValue",
                    defaultMessage: "Value",
                  })}
                  useInputDropdown
                  onDropdownSelect={(value) => handleSelectCurrency(fieldIndex, value)}
                  dropdownPlaceholder={intl.formatMessage({
                    id: "general.currency",
                    defaultMessage: "Currency",
                  })}
                  alignRight
                />
              </div>
              {fieldIndex !== fields.length - 1 && <Divider className="my-3xl" />}
            </div>
          ))}
          <Button
            className="mt-3xl"
            iconLeft={<PlusIcon />}
            label={intl.formatMessage({
              id: "new-request.packing-list.addCommodity",
              defaultMessage: "Add Commodity",
            })}
            variant="link-color"
            onClick={handleAddCommodity}
          />
        </>
      )}
      <Divider className="mt-3xl" />
    </div>
  );
}
